import { WHATSAPP_NUMBER } from '@/app/config.js';
import { formatDisplay } from '@/lib';
import FeedbackForm from '@/shared/FeedbackWidget/FeedbackForm';
import { Modal, PageHead } from '@/ui';
import { BugReportIcon, MailIcon, PhoneIcon } from '@/ui/icons';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import './Ayuda.css';
import EscribirSoporteModal from './EscribirSoporteModal';

// Story R12 — Ayuda y soporte. Product rules + three support cards:
//   1. Contacto     — teléfono + WhatsApp (one number, two channels)
//   2. Reportar un problema — reuses the shared FeedbackForm (POST /feedback)
//   3. Escribir a soporte   — support message form (EscribirSoporteModal)
// Both report/message forms reuse submitFeedback; neither is gated (the global
// FAB is the only experimental-features surface).

const REGLAS = [
  {
    id: 'suscribir',
    titulo: 'Suscribir causa',
    cuerpo:
      'Ingresa competencia, corte, tribunal, libro, rol y año. Recepthor trae la causa del Poder Judicial y sigue sus actuaciones.',
  },
  {
    id: 'actuacion-vs-diligencia',
    titulo: 'Actuación vs. diligencia',
    cuerpo:
      'La actuación es el acto del receptor (hoy: notificar). La diligencia es el encargo que le haces para que actúe.',
  },
  {
    id: 'una-diligencia-activa',
    titulo: 'Una diligencia activa por causa',
    cuerpo:
      'Cada causa tiene solo una diligencia en curso a la vez. Las cerradas quedan en el historial.',
  },
  {
    id: 'cotizar-elegir-pagar',
    titulo: 'Cotizar, elegir, pagar',
    cuerpo:
      'Los receptores ofertan con honorarios y fecha comprometida. Elige una oferta y págala; pagar una descarta el resto.',
  },
  {
    id: 'intermediacion',
    titulo: 'Recepthor intermedia el pago',
    cuerpo:
      'Paga honorarios, fee de Recepthor e IVA. Recepthor le transfiere los honorarios al receptor; el fee baja por tramos de volumen.',
  },
  {
    id: 'bloqueo-receptores',
    titulo: 'Bloquear un receptor',
    cuerpo:
      'Si un receptor falla, bloquéalo: deja de recibir tus cotizaciones, sin afectar las diligencias ya en curso.',
  },
];

export default function Ayuda() {
  const { setBreadcrumb } = useOutletContext();
  const [reportOpen, setReportOpen] = useState(false);
  const [escribirOpen, setEscribirOpen] = useState(false);
  const closeReport = () => setReportOpen(false);

  useEffect(() => {
    setBreadcrumb(['Ayuda']);
    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb]);

  // WhatsApp and phone share one number (WHATSAPP_NUMBER). Chilean numbers
  // render as "+56 9 XXXX XXXX"; a shape formatDisplay can't parse falls back
  // to the bare +<digits>.
  const phoneDisplay = formatDisplay(WHATSAPP_NUMBER) || `+${WHATSAPP_NUMBER}`;

  return (
    <div className="page reveal">
      <PageHead
        kicker="Ayuda"
        title="Ayuda"
        sub="Reglas del producto y soporte."
      />

      <div className="ayuda-body">
        {/* ── Reglas del producto (01–06) ── */}
        <section className="ayuda-rules">
          {REGLAS.map((regla, i) => (
            <div key={regla.id} className="ayuda-rule">
              <div className="ayuda-rule__n mono">
                {String(i + 1).padStart(2, '0')}
              </div>
              <p className="ayuda-rule__t">{regla.titulo}</p>
              <p className="ayuda-rule__d">{regla.cuerpo}</p>
            </div>
          ))}
        </section>

        {/* ── Soporte (3 tarjetas) ── */}
        <section>
          <div className="ayuda-soporte__head">
            <h3 className="ayuda-soporte__title">Soporte</h3>
          </div>
          <div className="ayuda-contact-grid">
            {/* 1 · Contacto — teléfono + WhatsApp (mismo número) */}
            <div className="ayuda-contact ayuda-contact--static">
              <span className="ayuda-contact__ic">
                <PhoneIcon size={18} />
              </span>
              <div>
                <div className="ayuda-contact__t">Teléfono y WhatsApp</div>
                <div className="ayuda-contact__d">{phoneDisplay}</div>
                <div className="ayuda-contact__links">
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    WhatsApp
                  </a>
                  <a href={`tel:+${WHATSAPP_NUMBER}`}>Llamar</a>
                </div>
              </div>
            </div>

            {/* 2 · Reportar un problema — bug report (FeedbackForm) */}
            <button
              type="button"
              className="ayuda-contact"
              onClick={() => setReportOpen(true)}
            >
              <span className="ayuda-contact__ic">
                <BugReportIcon size={18} />
              </span>
              <div>
                <div className="ayuda-contact__t">Reportar un problema</div>
                <div className="ayuda-contact__d">
                  Cuéntanos qué falló en la plataforma
                </div>
              </div>
            </button>

            {/* 3 · Escribir a soporte — support message (EscribirSoporteModal) */}
            <button
              type="button"
              className="ayuda-contact"
              onClick={() => setEscribirOpen(true)}
            >
              <span className="ayuda-contact__ic">
                <MailIcon size={18} />
              </span>
              <div>
                <div className="ayuda-contact__t">Escribir a soporte</div>
                <div className="ayuda-contact__d">
                  Consultas, sugerencias y mensajes
                </div>
              </div>
            </button>
          </div>
        </section>
      </div>

      <Modal open={reportOpen} onClose={closeReport} size="default">
        <Modal.Header title="Reportar un problema" onClose={closeReport} />
        <FeedbackForm variant="modal" onClose={closeReport} />
      </Modal>

      <EscribirSoporteModal
        open={escribirOpen}
        onClose={() => setEscribirOpen(false)}
      />
    </div>
  );
}
